import defaultAtavar from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { DropdownMenu } from '@/components/Dropdown/DropdownMenu';
import { DropdownMenuItem } from '@/components/Dropdown/DropdownMenuItem';
import { Image } from '@/components/Image';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { PageHeader } from '@/components/PageHeader';
import { useRole } from '@/hooks/UseRole';
import { assignSeat, removeSeat } from '@/services/stripeService';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { useUserStore } from '@/stores/useUserStore';
import { Company } from '@/types/company';
import { User } from '@/types/user';
import { isPC } from '@/utils/device';
import { handleError } from '@/utils/handleError';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { FaUndo, FaUser } from 'react-icons/fa';
import { FaEllipsisVertical } from 'react-icons/fa6';
import { IoMdPersonAdd } from 'react-icons/io';
import { MdDeleteForever } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

// Animations variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

// Composant UserCard pour toutes les tailles d'écran
const UserCard = ({
  user,
  onEdit,
  onAssignSeat,
  onRemoveSeat,
  onRestore,
  onDelete,
  currentUser,
  company,
  isAdmin,
}: {
  user: User;
  onEdit: (user: User) => void;
  onAssignSeat?: (id: number) => void;
  onRemoveSeat?: (id: number) => void;
  onRestore: (id: number) => void;
  onDelete: (id: number) => void;
  currentUser: User | null;
  company: Company | null;
  isAdmin: boolean;
}) => {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-orange-100 text-orange-700';
      case 'manager':
        return 'bg-blue-100 text-linko-light';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <motion.div
      className="flex h-48 flex-col rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md"
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Header avec avatar et infos principales */}
      <div className="flex items-start justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Image
            src={user.avatar}
            defaultImage={defaultAtavar}
            className="size-12 min-w-12 rounded-full"
            alt={user.firstname}
          />
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-semibold text-gray-900">
              {user.lastname} {user.firstname}
            </h3>
            {user.email && (
              <p className="truncate text-sm text-gray-600">{user.email}</p>
            )}
            {user.phone && (
              <p className="truncate text-sm text-gray-500">{user.phone}</p>
            )}
          </div>
        </div>

        <DropdownMenu
          trigger={
            <FaEllipsisVertical className="size-4 cursor-pointer text-gray-400" />
          }
        >
          {company?.available_seats &&
          company.available_seats > 0 &&
          company.active_subscription &&
          (company.active_subscription.user_id === currentUser?.id ||
            isAdmin) &&
          !user.seats?.find(seat => seat.is_active) ? (
            <DropdownMenuItem
              onClick={() => onAssignSeat?.(user.id)}
              className="flex w-full items-center gap-2 text-left"
            >
              {t('updateCompany:users.seats.assign')}
            </DropdownMenuItem>
          ) : company?.active_subscription?.user_id !== user?.id &&
            (company?.active_subscription?.user_id == currentUser?.id ||
              isAdmin) ? (
            <DropdownMenuItem
              onClick={() => onRemoveSeat?.(user.id)}
              className="flex w-full items-center gap-2 text-left"
            >
              {t('updateCompany:users.seats.remove')}
            </DropdownMenuItem>
          ) : null}
          {user.deleted_at ? (
            <DropdownMenuItem
              onClick={() => onRestore(user.id)}
              className="flex w-full items-center gap-2 text-left"
            >
              <FaUndo className="size-4" />
              {t('common:restore')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => onDelete(user.id)}
              className="flex w-full items-center gap-2 text-left text-red-500"
            >
              <MdDeleteForever className="size-4" />
              {t('common:delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenu>
      </div>

      {/* Role badge */}
      <div className="mt-3 flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleColor(user.role)}`}
        >
          {t(`common:roles.${user.role}`)}
        </span>
        {user.deleted_at && (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
            {t('common:deleted.user')}
          </span>
        )}
      </div>

      {/* Action principale */}
      <button
        onClick={() => onEdit(user)}
        className="mt-auto w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        disabled={!!user.deleted_at}
      >
        {user.deleted_at ? t('common:deleted.user') : t('common:edit')}
      </button>
    </motion.div>
  );
};

export const ListUsers = () => {
  const navigate = useNavigate();
  const {
    users,
    loading,
    listUsers,
    deleteUser,
    restoreUser,
    filters,
    setFilters,
  } = useUserStore();
  const isAdmin = useRole('admin');
  const { user: currentUser } = useAuthStore();
  const { company, showCompany } = useCompanyStore();

  useEffect(() => {
    const fetchUsers = async () => {
      if (filters.current_page == 1) {
        const perPage = isPC() ? 20 : 10;

        if (filters.per_page !== perPage) {
          setFilters({ per_page: perPage });
        }

        await listUsers({
          per_page: perPage,
          current_page: filters.current_page,
          sort_field: filters.sort_field,
          sort_order: filters.sort_order,
          paginate: 1,
          company_id: isAdmin ? undefined : currentUser?.company?.id,
        });
      }
    };
    fetchUsers();
  }, [
    filters.current_page,
    filters.per_page,
    filters.sort_field,
    filters.sort_order,
    isAdmin,
    currentUser,
    listUsers,
    setFilters,
  ]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    const fetchCompany = async () => {
      if (currentUser?.company?.id !== undefined) {
        await showCompany(currentUser?.company?.id);
      }
    };
    fetchCompany();
  }, [showCompany, currentUser, isAdmin]);

  const fetchMoreData = async () => {
    if (loading || (filters.current_page || 1) >= (filters.last_page || 1)) {
      return;
    }

    const nextPage = (filters.current_page || 1) + 1;
    await listUsers({
      ...filters,
      current_page: nextPage,
      paginate: 1,
      company_id: isAdmin ? undefined : currentUser?.company?.id,
    });
  };

  const handleAssignSeat = async (id: number) => {
    if (!company?.active_subscription?.id) {
      return;
    }
    try {
      await assignSeat(company.active_subscription.id, id);
      listUsers({
        ...filters,
        current_page: filters.current_page,
        paginate: 1,
        company_id: isAdmin ? undefined : currentUser?.company?.id,
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleRemoveSeat = async (id: number) => {
    if (!company?.active_subscription?.id) {
      return;
    }
    try {
      await removeSeat(company.active_subscription.id, id);
      listUsers({
        ...filters,
        current_page: filters.current_page,
        paginate: 1,
        company_id: isAdmin ? undefined : currentUser?.company?.id,
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleRestoreUser = async (id: number) => {
    await restoreUser(id);
  };

  const handleDeleteUser = async (id: number) => {
    await deleteUser(id);
  };

  const handleEdit = (user: User) => {
    if (user.deleted_at) {
      return toast.error(t('common:deleted.user'));
    }
    navigate(`${user.id}`);
  };

  return (
    <div id="scrollableDiv" className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between lg:gap-0"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader title={t('listUsers:title')} count={filters.total} />
        <Button
          onClick={() => navigate(`create`)}
          className="flex w-full items-center justify-center gap-2 lg:w-auto"
        >
          <IoMdPersonAdd className="size-4" />
          {t('listUsers:create-user')}
        </Button>
      </motion.div>

      {/* Liste des utilisateurs */}
      {users.length === 0 && !loading ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="flex flex-col items-center justify-center px-4 pb-4"
        >
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-20 items-center justify-center rounded-full bg-gray-100">
              <FaUser className="size-10 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {t('listUsers:empty.title')}
            </h3>
            <p className="mb-6 max-w-sm text-sm text-gray-500">
              {t('listUsers:empty.description')}
            </p>
            <Button
              onClick={() => navigate('create')}
              className="bg-linko-light hover:bg-linko-dark"
            >
              {t('listUsers:create-user')}
            </Button>
          </div>
        </motion.div>
      ) : (
        <InfiniteScrollList
          dataLength={users.length}
          next={fetchMoreData}
          hasMore={(filters.current_page || 1) < (filters.last_page || 1)}
          total={users.length}
          scrollableTarget="scrollableDiv"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="px-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence>
                {users.map(user => (
                  <motion.div key={user.id} variants={itemVariants}>
                    <UserCard
                      user={user}
                      onEdit={handleEdit}
                      onAssignSeat={handleAssignSeat}
                      onRemoveSeat={handleRemoveSeat}
                      onRestore={handleRestoreUser}
                      onDelete={handleDeleteUser}
                      currentUser={currentUser}
                      company={company}
                      isAdmin={isAdmin}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </InfiniteScrollList>
      )}
    </div>
  );
};
